import { useQuery } from '@tanstack/react-query';
import { Lightbulb, UserPlus, MessageCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getLeadContext } from '@/services/ai-context.service';
import { FormattedText } from '@/components/ui/FormattedText';

interface Props {
  leadId: number;
  /**
   * This endpoint generates its summary with an LLM call, so it stays off
   * until the user asks the assistant for something.
   */
  enabled?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700 ring-blue-200',
  contacted: 'bg-amber-50 text-amber-700 ring-amber-200',
  qualified: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  converted: 'bg-green-50 text-green-700 ring-green-200',
  dead: 'bg-slate-50 text-slate-500 ring-slate-200',
};

export function LeadContextPanel({ leadId, enabled = false }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-context', 'lead', leadId],
    queryFn: () => getLeadContext(leadId),
    staleTime: 30_000,
    enabled,
  });

  if (!enabled) return null;

  if (isLoading) return <SkeletonLoader />;

  if (isError || !data) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load lead context. Please try again.
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {/* AI Summary */}
      <div className="p-4">
        <div className="rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 p-4 text-white">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-indigo-200 mb-2">
            AI Summary
          </h3>
          <FormattedText text={data.aiSummary} className="text-sm leading-relaxed" />
        </div>
      </div>

      {/* Lead Info */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <UserPlus className="h-4 w-4 text-blue-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Lead Info
          </h4>
        </div>
        <div className="rounded-md border border-slate-100 bg-slate-50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-800">{data.name}</span>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                STATUS_COLORS[data.status ?? 'new'] ?? STATUS_COLORS.new,
              )}
            >
              {(data.status ?? 'new').charAt(0).toUpperCase() + (data.status ?? 'new').slice(1)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <dt className="text-xs text-slate-400">Source</dt>
              <dd className="text-sm text-slate-700">{data.source ?? 'Unknown'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">Days Since Created</dt>
              <dd className="text-sm text-slate-700">{data.daysSinceCreated}</dd>
            </div>
          </div>
        </div>
      </div>

      {/* Communication Count */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <MessageCircle className="h-4 w-4 text-teal-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Communications
          </h4>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-teal-50 text-teal-700">
            <span className="text-lg font-bold">{data.communicationCount}</span>
          </div>
          <div className="text-sm text-slate-600">
            {data.communicationCount === 0
              ? 'No communications yet'
              : `${data.communicationCount} total interaction${data.communicationCount !== 1 ? 's' : ''}`}
          </div>
        </div>
        {data.daysSinceCreated > 7 && data.communicationCount === 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
            <Clock className="h-3.5 w-3.5" />
            <span>No contact in over a week</span>
          </div>
        )}
      </div>

      {/* Suggested Actions */}
      {(data.suggestions ?? []).length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Suggested Actions
            </h4>
          </div>
          <ul className="space-y-2">
            {(data.suggestions ?? []).map((action, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="rounded-lg bg-gradient-to-br from-indigo-200 to-purple-200 h-28" />
      <div className="rounded-md bg-slate-100 h-28" />
      <div className="space-y-2">
        <div className="h-3 w-28 bg-slate-200 rounded" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-slate-200" />
          <div className="h-4 w-32 bg-slate-100 rounded" />
        </div>
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-full bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}
